import { Navigate } from "react-router";
import { useAuthContext } from "@/hooks/useAuthContext";
import { Outlet, useLocation } from "react-router";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { shouldEnforceProfileCompletion } from "@/utils/profileEnforcement";
import { NoOfficeScreen } from "./NoOfficeScreen";

export const ProtectedRoutes = () => {
  const {
    user,
    loadingUserData,
    getAccountId,
    isUserSuperAdmin,
    backendReachable,
    connectorDenied,
    needsPhoneForRegistration,
  } = useAuthContext();
  const location = useLocation();
  const accountId = getAccountId();

  // Fetch account data to check profile completion status.
  // Skip while the backend is down so this query cannot trap the app on "טוען...".
  const {
    data: account,
    isLoading: isLoadingAccount,
    isSuccess,
  } = useGetAccountByIdQuery(
    { accountId: accountId! },
    { skip: !accountId || !user || !backendReachable || !!connectorDenied },
  );

  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  // Firebase user exists but Nest refused to create an office without a phone.
  // Send them back to /login where the phone-only finish step lives — do not
  // show the «not linked to an office / contact admin» dead-end.
  if (user && needsPhoneForRegistration) {
    const redirect = location.pathname + location.search;
    const qs =
      redirect && redirect !== "/"
        ? `?redirect=${encodeURIComponent(redirect)}`
        : "";
    return <Navigate to={`/login${qs}`} replace />;
  }

  if (user && !backendReachable) {
    return <NoOfficeScreen />;
  }

  // Plan lacks webapp-connector (CONNECTOR_GATE_MODE=strict)
  if (user && connectorDenied) {
    return <NoOfficeScreen mode="connector_denied" />;
  }

  if (isLoadingAccount) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the original URL
  if (!user) {
    const currentPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  // If authenticated but no office — show contact admin screen (super admins bypass)
  if (!accountId && !isUserSuperAdmin()) {
    return <NoOfficeScreen />;
  }

  // Enforce profile completion after 24 hours
  if (isSuccess && shouldEnforceProfileCompletion(account, location.pathname)) {
    return <Navigate to="/account" replace state={{ from: location.pathname, enforced: true }} />;
  }

  // User is authenticated — let through (account data is optional context, not a gate)
  return <Outlet />;
};

export const AdminRoutes = () => {
  const { user } = useAuthContext();
  // TODO: implement admin check:
  // return user?.isAdmin === true ? <Outlet /> : <Navigate to="/admin-only" replace />;
  return user ? <Outlet /> : <Navigate to="/admin-only" replace />;
};

export const SuperAdminRoutes = () => {
  const { loadingUserData, isUserSuperAdmin } = useAuthContext();

  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  // Non–super-admins must not land on /unauthorized (dead-end after logout
  // from /super-admin with ?redirect=/super-admin). Send them home instead.
  if (isUserSuperAdmin()) {
    return <Outlet />;
  }
  return <Navigate to="/" replace />;
};
